import { useEffect, useRef } from 'react';
import { API_URL } from '../config';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const MAX_SSE_RETRIES = 5;
const POLLING_INTERVAL_MS = 15000;
const SSE_RECOVERY_INTERVAL_MS = 5 * 60 * 1000;

const useCoursesSSE = (dispatch, refetchCourses) => {
  const refetchCoursesRef = useRef(refetchCourses);
  const warnedFallbackRef = useRef(false);

  useEffect(() => {
    refetchCoursesRef.current = refetchCourses;
  }, [refetchCourses]);

  useEffect(() => {
    let eventSource;
    let intervalId;
    let retryCount = 0;
    let reconnectTimeout;
    let recoveryTimeout;
    let disposed = false;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (recoveryTimeout) {
        clearTimeout(recoveryTimeout);
        recoveryTimeout = null;
      }
    };

    const warnPollingFallbackOnce = () => {
      if (warnedFallbackRef.current || process.env.NODE_ENV === 'production') {
        return;
      }
      warnedFallbackRef.current = true;
      console.warn('Course SSE unavailable; using polling fallback for catalog updates.');
    };

    const startPolling = (enableRetry = true) => {
      if (intervalId) return;

      intervalId = setInterval(async () => {
        if (!refetchCoursesRef.current) return;
        try {
          await refetchCoursesRef.current();
        } catch (pollingError) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Course catalog polling error:', pollingError);
          }
        }
      }, POLLING_INTERVAL_MS);

      if (enableRetry && window.EventSource) {
        recoveryTimeout = setTimeout(() => {
          if (disposed) return;
          stopPolling();
          retryCount = 0;
          connectSSE();
        }, SSE_RECOVERY_INTERVAL_MS);
      }
    };

    const connectSSE = () => {
      if (disposed) return;
      stopPolling();

      if (!window.EventSource) {
        warnPollingFallbackOnce();
        startPolling(false);
        return;
      }

      eventSource = new EventSource(`${API_URL}/api/events/cursos`, { withCredentials: true });

      eventSource.onopen = () => {
        retryCount = 0;
        warnedFallbackRef.current = false;
      };

      const handleMessage = (type) => (event) => {
        retryCount = 0;
        try {
          const data = JSON.parse(event.data);
          dispatch({ type, payload: data.curso });
        } catch (parseError) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error parsing course SSE payload', parseError);
          }
        }
      };

      eventSource.addEventListener('curso:created', handleMessage('ADD_COURSE'));
      eventSource.addEventListener('curso:updated', handleMessage('UPDATE_COURSE'));
      eventSource.addEventListener('curso:deleted', handleMessage('DELETE_COURSE'));

      eventSource.onerror = () => {
        eventSource.close();

        if (retryCount < MAX_SSE_RETRIES) {
          const backoff = Math.min(INITIAL_BACKOFF_MS * (2 ** retryCount), MAX_BACKOFF_MS);
          retryCount += 1;
          reconnectTimeout = setTimeout(connectSSE, backoff);
          return;
        }

        warnPollingFallbackOnce();
        startPolling();
      };
    };

    connectSSE();

    return () => {
      disposed = true;
      if (eventSource) eventSource.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      stopPolling();
    };
  }, [dispatch]);
};

export default useCoursesSSE;
